import React, { useEffect, useState, useCallback, useContext } from 'react'
import { CHART_TYPES, CHART_MODES, chartMock } from './mock'
import './ClearTheCharts.scss'
import {
  getChart,
  getUserDataByKey,
  getUserStats,
  getPreflopData,
  getCurrentChartProgress,
} from './api'
import { usePrevious } from '../../hooks/usePrevious'
import { isEqual } from 'lodash'
import { useLocation } from 'react-router-dom'
import FilterToggle from './components/FilterToggle'
import { AppContext } from '../../AppContext'
import TableContainer from './containers/TableContainer'
import { ClearTheChartsContext } from './ClearTheChartsContext'
import ChartOptions from './components/ChartOptions'
import ChartReports from './components/ChartReports'
import ChartStatsView from './components/ChartStatsView'
import Resume from './components/Resume'
import GridIcon from '../../icons/grid.svg'
import { getMediaUrl } from '../../utils/utils'
import MistakesRow from './components/MistakesRow'
import { prepareUserStatsData } from './utils'

const PageTitle = ({
  toggleFilters,
  isFiltersExpanded,
  setIsBottomSheetOpen,
  chartId,
  showMistakes,
}) => {
  const renderMistakes = () => {
    if (!showMistakes) return null
    return (
      <div
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
          columnGap: '10px',
        }}
      >
        <MistakesRow chartId={chartId} />
      </div>
    )
  }

  return (
    <div className="ClearTheCharts__header">
      <h2 className={'ClearTheCharts__title'}>
        Clear The Chart
        <span style={{ fontSize: '18px', lineHeight: '28px' }}>(beta)</span>
      </h2>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '10px',
        }}
      >
        <FilterToggle
          isFiltersExpanded={isFiltersExpanded}
          toggleFilters={toggleFilters}
        />
        <div
          className="ClearTheCharts__bottom-sheet-button"
          onClick={() => setIsBottomSheetOpen(true)}
        >
          <img
            alt=""
            style={{ widht: '28px', height: '28px' }}
            src={getMediaUrl(GridIcon)}
          />
        </div>
        {renderMistakes()}
      </div>
    </div>
  )
}

export const ClearTheCharts = () => {
  const [loading, setLoading] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({
    chartType: 'MTTs Full Ring',
    stack: null,
    position: null,
    action: null,
    oppos: null,
  })
  const [chartData, setChartData] = useState({
    chartHTML: null,
    chartName: '',
  })
  const [filters, setFilters] = useState({
    actions: [],
    opponents: [],
    positions: [],
    stackSizes: [],
  })
  const [config, setConfig] = useState({
    colors: null,
    blindRestrictions: null,
    huRestrictions: null,
    icmRestrictions: null,
  })

  const [filterMocks, setFilterMocks] = useState({
    oppPositions: [],
    vsActions: [],
  })
  const { stackSizes, positions, opponents, actions } = filters

  const [filtersMode, setFiltersMode] = useState('New Game')

  const [mistakesCount, setMistakesCount] = useState(0)
  const [showMistakes, setShowMistakes] = useState(false)

  const [gameMode, setGameMode] = useState('table')

  const { user } = useContext(AppContext)
  const { state, dispatch } = useContext(ClearTheChartsContext)

  const { type: userAccess } = user

  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true)

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const { search } = useLocation()

  const getKeyData = async () => {
    try {
      const response = await getUserDataByKey({ key: 'gto_sweeper' })
      const { data } = response

      if (!data) return

      const { data_value } = data

      if (!data_value) return

      const valueToSet = JSON.parse(data_value)

      if (!valueToSet || Object.keys(valueToSet).length === 0) {
        return null
      }

      return valueToSet

    } catch (e) {
      console.log(e)
    }
  }
  const setShowAccessPopup = (requiredAccess = 'premium') => {
    window.showPopupPlan({
      title: 'Start your PokerCoaching membership today to unlock this content!',
      planSet:
        requiredAccess === 'pc'
          ? 'standard-premium'
          : requiredAccess === 'pcp'
          ? 'premium'
          : 'free',
      userAccess: user.type,
    })
  }

  const populateUserStats = async () => {
    try {
      const promises = [getUserStats(), getCurrentChartProgress(), getKeyData()]
      const [
        userStats,
        currentChartProgressData,
        legacyData,
      ] = await Promise.all(promises)

      const userStatsData = prepareUserStatsData(userStats, legacyData)

      if (userStatsData && currentChartProgressData?.length)
        dispatch({
          type: 'set-user-sweeper-stats',
          payload: {
            userStats: userStatsData,
            currentChartProgress: currentChartProgressData,
          },
        })
    } catch (e) {
      console.log(e)
    }
  }

  const getPreflopChartData = useCallback(async () => {
    try {
      const data = await getPreflopData()
      if (data) {
        const { data: preflopChartsData, settings } = data
        const {
          color_pallet: colors,
          access_blind_restrict: blindRestrictions,
          access_hu_restrict: huRestrictions,
          access_icm_restrict: icmRestrictions,
        } = settings

        dispatch({ type: 'set-preflop-charts', payload: preflopChartsData })

        setConfig({
          colors,
          blindRestrictions,
          huRestrictions,
          icmRestrictions,
        })
      }
    } catch (e) {
      console.log(e)
    }
  }, [setConfig, dispatch])

  useEffect(() => {
    getPreflopChartData()
    populateUserStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getInitialData = async (type) => {
    if (loading) return
    setLoading(true)
    const data = await getChart({
      chartMode: CHART_MODES.GTO,
      type: CHART_TYPES[type],
    })
    const {
      init_filter: { stacks, positions, oppPositions, vsActions },
    } = data
    setFilterMocks({
      oppPositions,
      vsActions,
    })
    const stackSizes = Object.values(stacks).sort((a, b) => a - b)

    setFilters({ ...filters, stackSizes, positions })
    setChartData({ chartHTML: chartMock })

    setSelectedFilters({
      ...selectedFilters,
      chartType: type,
      stack: null,
      position: null,
      action: null,
      oppos: null,
    })
    setLoading(false)
  }

  const prevSelectedFilters = usePrevious(selectedFilters)
  const prevChartType = usePrevious(selectedFilters.chartType)

  const getData = useCallback(async () => {
    if (selectedFilters.chartType !== prevChartType) return
    if (!isEqual(prevSelectedFilters, selectedFilters)) {
      setLoading(true)

      const data = await getChart({
        chartMode: CHART_MODES.GTO,
        type: CHART_TYPES[selectedFilters.chartType],
        filter: selectedFilters,
      })
      const {
        chart_title: chartName,
        filter_curr: filterCurr,
        filter: { actions_json: actions, oppos_json: opponents },
        chart_data: chartData,
      } = data

      let filterChartType = filterCurr.chartType
      if (filterChartType === 'Cash Game 8max') filterChartType = 'CASH GAME'
      setSelectedFilters({ ...filterCurr, chartType: filterChartType })
      setChartData({ chartData, chartName })
      setFilters({ ...filters, actions, opponents })
      setLoading(false)
      if (actions.includes('RFI') && !selectedFilters.action)
        setSelectedFilters({
          ...filterCurr,
          action: 'RFI',
          chartType: filterChartType,
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, prevSelectedFilters])

  useEffect(() => {
    const typeParam = search.split('?type=')[1]
    let type = null
    if (typeParam) {
      type = typeParam.split('&')[0]
    }
    if (!type) {
      return
    }
    let typeValue = null
    switch (type) {
      case 'mttsfullring':
        typeValue = 'MTTs Full Ring'
        break
      case 'cashgame':
        typeValue = 'CASH GAME'
        break
      case 'mttshunewante':
        typeValue = 'MTTs HU NEW ANTE'
        break
      case 'mttshunew':
        typeValue = 'MTTs HU NEW'
        break
      default:
        break
    }
    setSelectedFilters({ ...selectedFilters, chartType: typeValue })
    getInitialData(typeValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  useEffect(() => {
    if (!search) getInitialData(selectedFilters.chartType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters.chartType])

  useEffect(() => {
    if (selectedFilters.stack && !loading) getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters])

  const renderData = () => {
    switch (gameMode) {
      case 'table':
      case 'tableResume':
        return (
          <div style={{ width: '100%' }}>
            <TableContainer
              colors={config.colors}
              chartData={chartData}
              onBottomSheetDismiss={() => setIsBottomSheetOpen(false)}
              isBottomSheetOpen={isBottomSheetOpen}
              selectedFilters={selectedFilters}
              setShowMistakes={setShowMistakes}
              setMistakesCount={setMistakesCount}
              mistakesCount={mistakesCount}
              isResume={gameMode === 'tableResume'}
            />
          </div>
        )
      case 'chartStats':
        return <ChartStatsView colors={config.colors} />
      default:
        return null
    }
  }

  const { currentChartProgress } = state

  let isResumeActive = false

  if (currentChartProgress)
    for (const chartData of Object.values(currentChartProgress)) {
      const { completedHands } = chartData

      if (Object.keys(completedHands || {}).length) isResumeActive = true
    }

  return (
    <div className="ClearTheCharts">
      <PageTitle
        isFiltersExpanded={isFiltersExpanded}
        chartId={chartData?.chartData?.id}
        showMistakes={showMistakes}
        toggleFilters={() => {
          setIsFiltersExpanded(!isFiltersExpanded)
        }}
        setIsBottomSheetOpen={setIsBottomSheetOpen}
      />
      <div
        className={`ClearTheCharts__container ${
          isFiltersExpanded ? 'expanded' : ''
        }`}
        style={{ display: 'flex', flexDirection: 'row' }}
      >
        <div
          className={`ClearTheCharts__chart-filters ${
            isFiltersExpanded ? 'expanded' : ''
          }`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginRight: '40px',
            flexGrow: 1,
            rowGap: '15px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              margin: '-9px -24px',
            }}
          >
            <div
              onClick={() => {
                setFiltersMode('New Game')
                setGameMode('table')
              }}
              style={{
                backgroundColor: '#0085FF',
                display: 'flex',
                width: '100%',
                borderRadius: ' 10px 0px 0px 0px',
                padding: '10px',
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  color: filtersMode === 'New Game' ? 'white' : '#ccc',
                  fontFamily: ' Roboto, sans-serif',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                New Game
              </div>
            </div>
            {!!isResumeActive && (
              <div
                onClick={() => setFiltersMode('Resume')}
                style={{
                  backgroundColor: '#0085FF',
                  display: 'flex',
                  width: '100%',
                  padding: '10px',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    color: filtersMode === 'Resume' ? 'white' : '#ccc',
                    fontFamily: ' Roboto, sans-serif',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  Resume
                </div>
              </div>
            )}
            <div
              onClick={() => setFiltersMode('reports')}
              style={{
                backgroundColor: '#0085FF',
                display: 'flex',
                width: '100%',
                borderRadius: ' 0px 10px 0px 0px',
                padding: '10px',
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  color: filtersMode === 'reports' ? 'white' : '#ccc',
                  fontFamily: ' Roboto, sans-serif',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                Reports
              </div>
            </div>
          </div>
          {filtersMode === 'New Game' && (
            <GamesView
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              config={config}
              actions={actions}
              opponents={opponents}
              userAccess={userAccess}
              positions={positions}
              stackSizes={stackSizes}
              filterMocks={filterMocks}
              setShowAccessPopup={setShowAccessPopup}
            />
          )}
          {filtersMode === 'Resume' && (
            <Resume
              setSelectedFilters={setSelectedFilters}
              setGameMode={setGameMode}
              setFiltersMode={setFiltersMode}
              selectedFilters={selectedFilters}
            />
          )}
          {filtersMode === 'reports' && (
            <ChartReports setGameMode={setGameMode} />
          )}
        </div>
        {renderData()}
      </div>
    </div>
  )
}

const GamesView = ({
  selectedFilters,
  setSelectedFilters,
  config,
  filterMocks,
  actions,
  opponents,
  userAccess,
  positions,
  stackSizes,
  setShowAccessPopup,
}) => {
  return (
    <div>
      <ChartOptions
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        config={config}
        actions={actions}
        opponents={opponents}
        userAccess={userAccess}
        positions={positions}
        stackSizes={stackSizes}
        filterMocks={filterMocks}
        setShowAccessPopup={setShowAccessPopup}
      />
    </div>
  )
}
